import { Logger } from '../../utilities/Logger'

export class CountdownTimer {
  constructor (elem) {
    this.elem = elem
    this.startDate = this.parseDate(elem.dataset.countdownStart)
    this.endDate = this.parseDate(elem.dataset.countdownEnd)
    this.culture = elem.dataset.countdownCulture || 'en-GB'
    this.resources = JSON.parse(elem.dataset.countdownResources)
    this.days = elem.querySelector('[data-countdown-days]')
    this.hours = elem.querySelector('[data-countdown-hours]')
    this.mins = elem.querySelector('[data-countdown-mins]')
    this.secs = elem.querySelector('[data-countdown-secs]')
    this.logger = new Logger('CountdownTimer')

    this.intervalId = null
    this.previousTotalSeconds = null

    this.init()
  }

  init () {
    if (!this.validate()) {
      this.logger.error('Failed validation.')
      this.elem.remove()
      return
    }

    const target = this.endDate.getTime()
    this.intervalId = setInterval(() => {
      const now = Date.now()
      const diff = target - now

      if (diff <= 0) {
        this.onTick(0, 0, 0, 0)
        this.onEnd()
        return
      }

      const totalSeconds = Math.floor(diff / 1000)

      if (this.previousTotalSeconds !== totalSeconds) {
        this.previousTotalSeconds = totalSeconds

        const days = Math.floor(totalSeconds / (24 * 60 * 60))
        const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60

        this.onTick(days, hours, minutes, seconds)
      }
    }, 100)
  }

  destroy () {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
    this.elem.remove()
  }

  onTick (days, hours, mins, secs) {
    if (days > 0) {
      this.days.style.display = 'block'
      this.days.querySelector('strong').innerText = this.pad(days)
      this.days.querySelector('span').innerText = this.getDateSuffix('day', days)
    } else {
      this.days.style.display = 'none'
    }

    if (hours > 0) {
      this.hours.style.display = 'block'
      this.hours.querySelector('strong').innerText = this.pad(hours)
      this.hours.querySelector('span').innerText = this.getDateSuffix('hour', hours)
    } else {
      this.hours.style.display = 'none'
    }

    if (mins > 0) {
      this.mins.style.display = 'block'
      this.mins.querySelector('strong').innerText = this.pad(mins)
      this.mins.querySelector('span').innerText = this.getDateSuffix('minute', mins)
    } else {
      this.mins.style.display = 'none'
    }

    this.secs.querySelector('strong').innerText = this.pad(secs)
    this.secs.querySelector('span').innerText = this.getDateSuffix('second', secs)
  }

  onEnd () {
    this.logger.log('Countdown Ended.')
    this.destroy()
  }

  validate () {
    if (!this.startDate || !this.endDate) {
      this.logger.error('Start/End date are required.')
      return false
    }

    if (this.startDate > this.endDate) {
      this.logger.error('Start date cannot be greater than end date.')
      return false
    }

    if (this.startDate > new Date()) {
      this.logger.info('Start date is in the future. Countdown is not yet active.')
      return false
    }

    if (this.endDate < new Date()) {
      this.logger.info('End date has passed. Countdown has expired.')
      return false
    }

    return true
  }

  pad (n) {
    return String(n).padStart(2, '0')
  }

  parseDate (dateString) {
    if (!dateString) {
      return null
    }

    // Handle unix timestamps (storybook only)
    if (dateString.indexOf(' ') <= 0) {
      return new Date(parseInt(dateString))
    }

    // Parse in format 'dd/mm/yyyy hh:mm:ss'
    const [datePart, timePart] = dateString.split(' ')
    const [dd, mm, yyyy] = datePart.split('/').map(Number)
    const [hh, min, ss] = timePart.split(':').map(Number)

    return new Date(yyyy, mm - 1, dd, hh, min, ss)
  }

  getDateSuffix (type, value) {
    switch (type) {
      case 'day':
        return value === 1 ? this.getResource('CountdownTimer.DaySuffix') : this.getResource('CountdownTimer.DaysSuffix')
      case 'hour':
        return value === 1 ? this.getResource('CountdownTimer.HourSuffix') : this.getResource('CountdownTimer.HoursSuffix')
      case 'minute':
        return value === 1 ? this.getResource('CountdownTimer.MinuteSuffix') : this.getResource('CountdownTimer.MinutesSuffix')
      case 'second':
        return value === 1 ? this.getResource('CountdownTimer.SecondSuffix') : this.getResource('CountdownTimer.SecondsSuffix')
    }
  }

  getResource (key) {
    if (!this.resources) {
      return null
    }

    return this.resources.find(x => x.key === key)?.value || null
  }
}

export const setupCountdownTimers = function (parent) {
  const elems = parent.querySelectorAll('[data-countdown]')
  const countdownTimers = []
  for (const elem of elems) {
    countdownTimers.push(new CountdownTimer(elem))
  }
  return countdownTimers
}
